import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { groupBy, keyBy, mapValues, omitBy, uniqBy } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { SectionSectionGroup } from 'src/WebApiController';

import { EventVenueMapFormType } from '../../EventVenueMapModal';
import {
  getOrderedSectionGroups,
  reorderSectionGroups,
  toSectionGroupKey,
} from './SectionGroupPanelV2.utils';

export const DefaultSectionGroupColor = '#3E008E';

export type SectionSectionGroupWithId = SectionSectionGroup & { id: string };

export const DefaultSectionGroup: SectionSectionGroup = {
  sectId: 0,
  areaRank: 1,
  sectionGroupRank: 1,
  ticketClassName: null,
  sectionGroupAreaName: null,
  sectionGroupAreaType: null,
  ticketClassId: null,
  sectionGroupColor: DefaultSectionGroupColor,
};

export const useSectionGroupOperations = (
  sectionSectionGroupLookup: {
    [key: string]: SectionSectionGroup;
  },
  customSectionGroups: SectionSectionGroup[]
) => {
  const { setValue } = useFormContext<EventVenueMapFormType>();

  const groupIdToSections = useMemo(() => {
    return groupBy(
      Object.entries(sectionSectionGroupLookup).map(
        ([sectionId, sectionGroup]) => {
          return {
            groupId: toSectionGroupKey(sectionGroup),
            sectionId,
          };
        }
      ),
      'groupId'
    );
  }, [sectionSectionGroupLookup]);

  const sortabledSectionGroups: SectionSectionGroupWithId[] = useMemo(() => {
    return getOrderedSectionGroups(
      sectionSectionGroupLookup,
      customSectionGroups
    );
  }, [sectionSectionGroupLookup, customSectionGroups]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over == null || active.id === over.id) {
        return;
      }

      const oldIndex = sortabledSectionGroups.findIndex(
        (ro) => ro.id === active.id
      );
      const newIndex = sortabledSectionGroups.findIndex(
        (ro) => ro.id === over.id
      );

      const shiftedRankOverrides = arrayMove(
        sortabledSectionGroups,
        oldIndex,
        newIndex
      );

      // Update ranks based on new positions
      const updatedRankOverrides = keyBy(
        shiftedRankOverrides.map((item, index) => ({
          ...item,
          sectionGroupRank: index + 1,
        })),
        (ssg) => toSectionGroupKey(ssg)
      );

      const [newSectionSectionGroupLookup, newCustomSectionGroup] =
        reorderSectionGroups(
          sectionSectionGroupLookup,
          customSectionGroups,
          mapValues(updatedRankOverrides, 'sectionGroupRank')
        );

      setValue('sectionSectionGroupLookup', newSectionSectionGroupLookup);
      setValue('customSectionGroups', newCustomSectionGroup);
    },
    [
      customSectionGroups,
      sectionSectionGroupLookup,
      setValue,
      sortabledSectionGroups,
    ]
  );

  const onUpdateSectionGroup = useCallback(
    (key: string, updated: SectionSectionGroup) => {
      const updatedSectionSectionGroupLookup = mapValues(
        sectionSectionGroupLookup,
        (sectionGroup) => {
          if (toSectionGroupKey(sectionGroup) === key) {
            return {
              ...sectionGroup,
              sectionGroupAreaName: updated.sectionGroupAreaName,
              sectionGroupColor: updated.sectionGroupColor,
            };
          }
          return sectionGroup;
        }
      );
      setValue('sectionSectionGroupLookup', updatedSectionSectionGroupLookup);

      const updatedCustomSectionGroups = customSectionGroups.map(
        (sectionGroup) => {
          if (toSectionGroupKey(sectionGroup) === key) {
            return {
              ...sectionGroup,
              sectionGroupAreaName: updated.sectionGroupAreaName,
              sectionGroupColor: updated.sectionGroupColor,
            };
          }
          return sectionGroup;
        }
      );

      setValue('customSectionGroups', updatedCustomSectionGroups);
    },
    [customSectionGroups, sectionSectionGroupLookup, setValue]
  );

  const onDeleteSectionGroup = useCallback(
    (key: string) => {
      const updatedSectionSectionGroupLookup = omitBy(
        sectionSectionGroupLookup,
        (sectionGroup) => {
          return toSectionGroupKey(sectionGroup) === key;
        }
      );

      const updatedCustomSectionGroups = customSectionGroups.filter(
        (sectionGroup) => {
          return toSectionGroupKey(sectionGroup) !== key;
        }
      );

      const [newSectionSectionGroupLookup, newCustomSectionGroup] =
        reorderSectionGroups(
          updatedSectionSectionGroupLookup,
          updatedCustomSectionGroups
        );

      setValue('sectionSectionGroupLookup', newSectionSectionGroupLookup);
      setValue('customSectionGroups', newCustomSectionGroup);
    },
    [customSectionGroups, sectionSectionGroupLookup, setValue]
  );

  const onUnassignSections = useCallback(
    (sectionIds: number[]) => {
      const updatedLookup = { ...sectionSectionGroupLookup };
      for (const sectionId of sectionIds) {
        delete updatedLookup[sectionId];
      }

      const [newSectionSectionGroupLookup, newCustomSectionGroup] =
        reorderSectionGroups(updatedLookup, customSectionGroups);

      setValue('sectionSectionGroupLookup', newSectionSectionGroupLookup);
      setValue('customSectionGroups', newCustomSectionGroup);
    },
    [customSectionGroups, sectionSectionGroupLookup, setValue]
  );

  const onCreateSectionGroup = useCallback(
    (
      sectionIds: number[],
      sectionGroupAreaName: string,
      sectionGroupColor: string
    ) => {
      const newCustomSectionGroup = {
        ...DefaultSectionGroup,
        sectionGroupAreaName: sectionGroupAreaName,
        sectionGroupColor,
      } as SectionSectionGroup;

      const updatedLookup = { ...sectionSectionGroupLookup };
      for (const sectionId of sectionIds) {
        updatedLookup[sectionId] = newCustomSectionGroup;
      }

      const updatedCustomSectionGroups = uniqBy(
        [...(customSectionGroups || []), newCustomSectionGroup],
        (group) => toSectionGroupKey(group)
      );

      const [newSectionSectionGroupLookup, newCustomSectionGroups] =
        reorderSectionGroups(updatedLookup, updatedCustomSectionGroups);

      setValue('sectionSectionGroupLookup', newSectionSectionGroupLookup);
      setValue('customSectionGroups', newCustomSectionGroups);
    },
    [customSectionGroups, sectionSectionGroupLookup, setValue]
  );

  const onClearAllSectionGroups = useCallback(() => {
    setValue('customSectionGroups', []);
    setValue('sectionSectionGroupLookup', {});
  }, [setValue]);

  return {
    groupIdToSections,
    sortabledSectionGroups,
    handleDragEnd,
    onUpdateSectionGroup,
    onDeleteSectionGroup,
    onUnassignSections,
    onCreateSectionGroup,
    onClearAllSectionGroups,
  };
};
